import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { usePostHog } from 'posthog-js/react';
import { getDeviceInfo } from 'state/localStorageTyped';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { RecurlyAccountInformation } from 'features/payments/components/RecurlyAccountInformation';
import {
  useDonationState,
  DonationState,
} from 'features/payments/hooks/useDonationState';
import { useLocationSearchParams } from 'hooks/useLocationSearchParams';
import { ENV } from 'shared/ENV';
import {
  ProcessDonationPaymentTokenData,
  ProcessInitialDonationPaymentTokenData,
  ProcessPaymentState,
} from 'shared/types';
import { useSavedPaymentMethod } from 'hooks/useSavedPaymentMethod';
import { setAnalyticsUser } from 'shared/analytics';
import { usePaymentApi } from 'hooks/usePaymentApi';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { getPaywallLink } from 'features/membership/membership.utils';
import { Loader } from 'components/Loader';
import { TRACKING_ID_PARAM } from '../../../constants';

const getInitialState = (
  donationState: DonationState,
  params: { email: string; firstName?: string; lastName?: string },
): ProcessPaymentState => {
  const {
    clientToken,
    donationAmount,
    donationRenews,
    optInToUpdates,
    email,
    firstName,
    lastName,
    existingUser,
    verificationToken,
    userId,
  } = donationState;

  return {
    email: email || params.email,
    firstName: firstName || params.firstName || '',
    lastName: lastName || params.lastName || '',
    clientToken,
    optInToUpdates,
    transactionAmount: donationAmount,
    donationRenews,
    existingUser,
    verificationToken,
    userId,
  };
};

const Content = (): JSX.Element | null => {
  const { t } = useTranslation();
  const params = useLocationSearchParams();
  const history = useHistory();
  const posthog = usePostHog();

  const emailParam = decodeURIComponent(params.get('email') || '');
  const donationAmountParam = parseFloat(params.get('donationAmount') ?? '0');
  const donationRenewsParam = params.get('donationRenews') === 'true';
  const clientTokenParam = params.get('clientToken') || undefined;
  const verificationTokenParam = params.get('verificationToken') || undefined;
  const userIdParam = params.get('userId')
    ? parseInt(params.get('userId') as string, 10)
    : undefined;
  const posthogIdParam = params.get(TRACKING_ID_PARAM);

  useEffect(() => {
    // set the user id for analytics when purchase membership flow continues on web
    if (userIdParam) {
      setAnalyticsUser({ userId: userIdParam.toString() });
    }
    // Re-identify with PostHog if coming from mobile app
    if (posthogIdParam) {
      posthog.identify(posthogIdParam);
    }
  }, [userIdParam, posthogIdParam, posthog]);

  const stateUpdateData: Partial<DonationState> | undefined = useMemo(() => {
    return clientTokenParam || verificationTokenParam
      ? {
          donationAmount: donationAmountParam,
          donationRenews: donationRenewsParam,
          clientToken: clientTokenParam,
          verificationToken: verificationTokenParam,
          userId: userIdParam,
          existingUser: Boolean(emailParam),
          fromMobile: true,
        }
      : undefined;
  }, [
    clientTokenParam,
    donationAmountParam,
    donationRenewsParam,
    emailParam,
    userIdParam,
    verificationTokenParam,
  ]);

  const { updateDonationState, resetDonationState, ...donationState } =
    useDonationState({
      stateUpdateData,
    });

  const initialState = getInitialState(donationState, {
    email: emailParam,
    firstName: params.get('firstName') || undefined,
    lastName: params.get('lastName') || undefined,
  });

  const userId = donationState.userId || userIdParam;
  const verificationToken =
    donationState.verificationToken || verificationTokenParam;

  const { savedPaymentMethod, isLoading: isLoadingPaymentMethod } =
    useSavedPaymentMethod({ userId, verificationToken });

  const { processDonationPaymentMutation } = usePaymentApi<
    ProcessInitialDonationPaymentTokenData | ProcessDonationPaymentTokenData
  >({
    onSuccess: () => {
      history.push('/donate/success');
    },
  });

  if (isLoadingPaymentMethod) {
    return <Loader disableBackground />;
  }

  if (!initialState.transactionAmount) {
    // Something has gone horribly wrong, we'll reset their local storage, restart the process, and hope for the best
    Sentry.captureMessage('Donation transaction amount unset', {
      level: 'warning',
    });
    resetDonationState();
    const paywallLink = getPaywallLink({
      hasActiveMembership: false,
      source: 'donate_account_info_error',
    });
    history.push(paywallLink.url);
    enqueueSnackbar(t('donationCheckout.unknownError'), { variant: 'error' });
    return null;
  }

  /**
   * IAP membership accounts return an object with all properties set to "null"
   */
  const paymentMethod =
    savedPaymentMethod && Object.values(savedPaymentMethod).some(Boolean)
      ? savedPaymentMethod
      : undefined;

  return (
    <>
      <Helmet>
        <title>{t('donationAccountInformation.title')}</title>
      </Helmet>
      <SimplePageLayout>
        <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
          <Elements>
            <RecurlyAccountInformation<
              | ProcessInitialDonationPaymentTokenData
              | ProcessDonationPaymentTokenData
            >
              transactionType="donation"
              initialState={initialState}
              updateState={updateDonationState}
              processPaymentMutation={processDonationPaymentMutation}
              paymentMethod={paymentMethod}
              checkoutConfig={{
                title: initialState.donationRenews
                  ? t('donate.monthlyDonation')
                  : t('donate.oneTimeDonation'),
                description: t('donate.footer'),
              }}
            />
          </Elements>
        </RecurlyProvider>
      </SimplePageLayout>
    </>
  );
};

export const DonateAccountInformation = (): JSX.Element => {
  const { t } = useTranslation();
  const deviceInfo = getDeviceInfo();
  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  return (
    <PageLayout
      data-cname="DonationAccountInformationPage"
      allowAppBanner={false}
      headerBarProps={{
        action: isMobilePlatform ? 'noControls' : 'back',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
